import { action, observable } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import type { Question } from '@/features/jobs/questions/questions'
import { type QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import type { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * View-model for questions with editing capabilities.
 */
export class QuestionEditorViewModel<
  T extends Question = Question,
> implements ActivationLifecycle {
  /**
   * Question ID.
   */
  @observable accessor id = ''

  /**
   * The question text.
   */
  @observable accessor title = ''

  /**
   * The question description.
   */
  @observable accessor description = ''

  /**
   * Validation errors from the last {@link validate}.
   */
  @observable accessor validationErrors: readonly string[] = []

  /**
   * Answer editor view model.
   */
  readonly editor

  constructor(editor: QuestionAnswerEditorViewModelBase<T>) {
    this.editor = editor
  }

  /** @inheritDoc */
  @action.bound
  activate() {
    this.editor.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this.editor.deactivate()
  }

  /**
   * Sets the view model's properties from the question.
   *
   * @param question
   */
  @action.bound
  set(question: T) {
    this.id = Id.assert(question.id)
    this.title = question.title
    this.description = question.description ?? ''
    this.editor.set(question)
  }

  /**
   * Validates the editor's answer and keeps the errors for display.
   */
  @action.bound
  validate(): QuestionValidationResult {
    const result = this.editor.validate()
    this.validationErrors = result.type === 'Failed' ? result.messages : []
    return result
  }
}
